// src/components/achievements/GroupCard.tsx
import React, { useState } from 'react';
import type { Group } from '../../services/achievementService';
import { getBungieIconUrl } from '../../services/bungieIconService';
import { getDefaultCategoryIcon } from '../utils/iconHelper';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext'; // Füge diesen Import hinzu

type Props = {
  group: Group;
  onClick: (group: Group) => void;
  userIsAuthenticated?: boolean;
};

const GroupCard: React.FC<Props> = ({ group, onClick, userIsAuthenticated = false }) => {
  const { t } = useLanguage(); // Füge dies hinzu, um die Übersetzungsfunktion zu nutzen
  const [iconError, setIconError] = useState(false);
  
  // Get the Bungie icon URL through our proxy if available
  const iconUrl = !iconError && group.icon_url ? getBungieIconUrl(group.icon_url) : null;
  
  // Calculate group completion metrics, if category and achievement data is available
  const allAchievements = group.categories
    .flatMap(cat => [
      ...cat.achievements || [],
      ...cat.subcategories?.flatMap(subcat => subcat.achievements || []) || []
    ]);
  
  // Safe calculation of values
  const totalAchievements = allAchievements.length;
  const completedAchievements = totalAchievements > 0 ? 
    allAchievements.filter(a => a.is_completed).length : 0;
  
  // Check if fully completed
  const isFullyCompleted = totalAchievements > 0 && completedAchievements === totalAchievements;
  
  // Avoid division by zero
  const completionPercentage = totalAchievements > 0
    ? Math.round((completedAchievements / totalAchievements) * 100)
    : 0;

  return (
    <div
      key={group.id}
      onClick={() => onClick(group)}
      className="bg-gray-800 rounded-lg overflow-hidden cursor-pointer transition-all transform hover:scale-105 hover:shadow-lg"
    >
      <div className="aspect-w-16 aspect-h-9 relative p-6">
        {/* Background image for the card */}
        <div
          className={`w-full h-full bg-center absolute inset-0 ${
            iconUrl ? 'bg-cover' : 'bg-gray-700'
          }`}
          style={iconUrl ? { backgroundImage: `url(${iconUrl})` } : {}}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
        </div>
        
        {/* Card content */}
        <div className="relative z-10 flex flex-col h-full justify-end">
          <div className="flex items-center">
            <h2 className="text-xl font-bold text-white">{group.display_name}</h2>
            {/* Show completion checkmark for authenticated users */}
            {userIsAuthenticated && isFullyCompleted && (
              <CheckCircleIcon className="h-5 w-5 text-destiny-green ml-2" />
            )}
          </div>
          <p className="text-sm text-gray-300 mt-1 line-clamp-2">{group.description}</p>
          
          {/* Achievement count and progress - consistent with CategoryCard */}
          <div className="mt-2 flex items-center text-xs text-gray-400">
            <span>{totalAchievements} {t('achievements.title')}</span>
            {userIsAuthenticated && totalAchievements > 0 && (
              <span className="ml-2 text-destiny-gold">
                ({completedAchievements}/{totalAchievements}, {completionPercentage}%)
              </span>
            )}
          </div>
          
          {/* Progress indicator - consistent with CategoryCard */}
          <div className="mt-2 min-h-[4px]">
            {userIsAuthenticated && totalAchievements > 0 ? (
              <div className="w-full bg-gray-700 bg-opacity-60 rounded-full h-1">
                <div
                  className={`h-1 rounded-full ${isFullyCompleted ? 'bg-destiny-green' : 'bg-destiny-gold'}`}
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
            ) : (
              // Empty container for consistent layout
              <div className="w-full h-1"></div>
            )}
          </div>
        </div>
      </div>
      
      {/* Icon as overlay element */}
      <div className="absolute top-3 right-3 w-12 h-12">
        <img
          src={iconUrl || getDefaultCategoryIcon(group.display_name)}
          alt={group.display_name}
          className="w-full h-full object-contain"
          onError={(e) => {
            setIconError(true);
            const target = e.target as HTMLImageElement;
            target.onerror = null;
            target.src = getDefaultCategoryIcon(group.display_name);
          }}
        />
      </div>
    </div>
  );
};

export default GroupCard;